import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlan } from '../plan/usePlan';
import { clearSession } from '../session/vaultSession';
import { announceForget } from '../session/tabHandshake';
import { getAuth } from '../session/authSession';
import { forgetBrowser, guardLabel, rememberedGuard } from '../vault/trustFlow';
import type { GuardKind } from '../vault/trustedBrowser';
import type { Arc } from '../plan/arc';
import { flat } from '../entity/entity';
import { isArchived } from '../plan/arc';
import { ArchivedGlyph, StatusGlyph } from '../board/StatusGlyph';
import { arcHref } from '../routes/ArcRoute';
import { bestMatches, rankMatches } from './fuzzy';
import { EMPTY_FOLDERS, trailOf } from '../entity/folders';
import type { Folder } from '../entity/folders';
import { Mark } from '../folders/Mark';
import { folderHref } from '../folders/routes';
import { useTheme } from '../design/useTheme';
import { useCatalog } from '../entity/catalogStore';
import { hrefFor } from '../routes/EntityPage';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { nameOf, SCRATCH_NAME } from '../workspaces/workspace';
import type { Workspace } from '../workspaces/workspace';
import { currentScratch, scratchCanvases } from '../workspaces/scratch';
import { nameTaken } from '../nook/newArc';
import { NookThumb, TintedNookGlyph } from '../workspaces/NookCanvas';
import { NewHolderDraft } from '../membership/NewHolderDraft';
import { createOffers, holderCandidates, holdersFor } from '../membership/membership';
import type { HolderCandidate, HolderKind } from '../membership/membership';
import { useSelection } from '../membership/selection';
import { useHolderWrites } from '../membership/useHolderWrites';

/**
 * ⌘K — three named registers, not a search field with a list under it.
 *
 * It does NOT search thoughts. The vault holds thousands of recordings and
 * the head only decrypts what it needs, so full-text search over the corpus
 * is a phone capability. The footer says so rather than returning a slow,
 * partial answer that looks like the whole one.
 *
 * Folders cost nothing to add and are the one folder surface with no name
 * problem: rows are the folder objects and navigation is by id, so the
 * ambiguity that can leave a chip inert never arises. They sit BELOW the arcs,
 * because the first row of the first non-empty register is what `↵` takes and
 * typing `web` should still open an arc.
 *
 * Typing filters all three registers at once, and the first row of the first
 * non-empty register is always what ↵ takes — so a query can be typed and
 * committed without ever looking at what is selected.
 */
interface Row {
  key: string;
  label: string;
  meta?: string;
  arc?: Arc;
  folder?: Folder;
  /** Where the page's selection can be added by this row; ⇧↵ or (+) does it. */
  holder?: HolderCandidate;
  /** A nook row: its tags tint the glyph, and an unnamed one shows its canvas for a label. */
  nook?: { tags: readonly string[]; unnamed: Workspace | null };
  /** The row that reveals the rest of its register; taking it leaves the palette open. */
  more?: boolean;
  run: () => void;
}

/** Stable identity: the memo below lists `arcs` as a dependency. */
const EMPTY: Arc[] = [];

const OPEN_EVENT = 'pondering:palette';

/** Rows a register shows before it folds the rest behind "N more". */
const SHOWN = 8;

/** Opens the palette from a control rather than ⌘K — the header's Search. */
export function openPalette(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function CommandPalette() {
  const { plan } = usePlan();
  const { theme } = useTheme();
  const arcs = plan.status === 'ready' ? plan.plan.arcs : EMPTY;
  // No load of its own: the tree hangs off the plan the board already read.
  const folders = plan.status === 'ready' ? plan.plan.registries.folders : EMPTY_FOLDERS;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const body = useRef<HTMLDivElement>(null);
  /** What is holding a key on this machine, if anything. Read when the palette
   *  opens because this row is where someone comes to check — and "forget the
   *  key" should say what it is about to forget. */
  const { items: picked, onUsed } = useSelection();
  const adding = picked.length > 0;
  const catalog = useCatalog();
  const nooks = useWorkspaces(open);
  const { addItems, createHolding, scratch, newScratch } = useHolderWrites();
  const [drafting, setDrafting] = useState<{ kind: HolderKind; name: string } | null>(null);
  const [guard, setGuard] = useState<GuardKind | null>(null);

  useEffect(() => {
    if (open) void rememberedGuard().then(setGuard);
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((o) => !o);
        setQuery('');
        setCursor(0);
        setExpanded(new Set());
      }
    };
    const onAsk = () => {
      setOpen(true);
      setQuery('');
      setCursor(0);
      setExpanded(new Set());
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onAsk);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onAsk);
    };
  }, []);

  // No load of its own. It used to open a second vault and parse the plan
  // again, which is how it came to show a different arc count from the board.

  const registers = useMemo(() => {
    const nooksReady = nooks.status === 'ready';
    const holders = adding
      ? holdersFor(picked, arcs, nooks.status === 'ready' ? nooks.workspaces : [], catalog)
      : [];
    const holderOf = new Map(holders.map((h) => [h.key, h]));
    const asRow = (arc: Arc): Row => ({
      key: arc.path,
      label: arc.title,
      meta: flat(arc.frontMatter['area']),
      arc,
      holder: holderOf.get(arc.path),
      run: () => navigate(arcHref(arc.name)),
    });

    // Filed-away arcs are still reachable — the palette is where you go when
    // you know the name — but they sit in a register of their own so a search
    // over live work is not half retired storylines.
    const live = arcs.filter((arc) => !isArchived(arc));
    const filed = arcs.filter(isArchived);
    const matchedArcs: Row[] = rankMatches(live, query, (a) => a.title).map(asRow);
    const matchedArchived: Row[] = rankMatches(filed, query, (a) => a.title).map(asRow);

    // The trail, not the counts: counts need the corpus and the catalog, and
    // this palette must not pull those in. It also disambiguates the two
    // folders that may share a name under different parents.
    const matchedFolders: Row[] = rankMatches(folders.all, query, (f) => f.name).map((folder) => ({
      key: folder.id,
      label: folder.name,
      meta: trailOf(folders, folder).slice(0, -1).map((step) => step.name).join(' / ') || undefined,
      folder,
      run: () => navigate(folderHref(folder.id)),
    }));

    const goTo: Row[] = bestMatches(
      [
        { key: 'desk', label: 'The desk', run: () => navigate('/') },
        { key: 'board', label: 'The plan board', meta: '⌘B', run: () => navigate('/plan') },
        ...(nooksReady && !adding
          ? scratchCanvases(nooks.workspaces).live.slice(0, 3).map((w) => ({
              key: `scratch-${w.path}`,
              label: nameOf(w, catalog).name,
              meta: w.path === currentScratch(nooks.workspaces)?.path ? 'scratch · current' : 'scratch',
              run: () => navigate(hrefFor(w.path)),
            }))
          : []),
        ...(query
          ? []
          : live
              .filter((a) => a.frontMatter['updated'])
              .sort((a, b) => flat(b.frontMatter['updated']).localeCompare(flat(a.frontMatter['updated'])))
              .slice(0, 3)
              .map((arc) => ({
                key: `recent-${arc.path}`,
                label: arc.title,
                meta: 'recent',
                arc,
                run: () => navigate(arcHref(arc.name)),
              }))),
      ] as Row[],
      query,
      (r) => r.label,
    );

    const doThings: Row[] = bestMatches(
      [
        ...(nooksReady && adding
          ? [{
              key: 'add-scratch',
              label: `Add to ${SCRATCH_NAME.toLowerCase()}`,
              meta: `${picked.length} selected`,
              run: () => {
                void scratch(nooks.workspaces, picked);
                onUsed();
              },
            }]
          : []),
        ...(nooksReady && !adding
          ? [{
              key: 'new-scratch',
              label: `New ${SCRATCH_NAME.toLowerCase()}`,
              meta: 'lay things out before naming them',
              run: () => void newScratch().then((to) => to && navigate(hrefFor(to))),
            }]
          : []),
        {
          key: 'new-note',
          label: 'New text note',
          meta: 'a plain page',
          run: () => navigate('/write'),
        },
        {
          key: 'disconnect',
          label: 'Forget the key and disconnect',
          meta: guard ? `remembered · ${guardLabel(guard)}` : 'this tab only',
          run: () => {
            // Forgetting has to reach what OUTLIVES the tab too, or the next
            // load quietly opens itself again and the command was a lie: the
            // sealed key here, the share behind it, and the read cache.
            void forgetBrowser(getAuth()?.idToken ?? null);
            // And what outlives it SIDEWAYS: a sibling tab still holding the
            // key would hand it to the next tab that opened.
            announceForget();
            clearSession();
            navigate('/connect');
          },
        },
      ] as Row[],
      query,
      (r) => r.label,
    );

    const liveNooks = (nooks.status === 'ready' ? nooks.workspaces : [])
      .filter((w) => !w.archived && !w.scratch && !w.inside)
      .map((w) => ({ path: w.path, label: nameOf(w, catalog).name, workspace: w }));
    const matchedNooks: Row[] = rankMatches(liveNooks, query, (n) => n.label).map((nook) => ({
      key: nook.path,
      label: nook.label,
      meta: 'nook',
      nook: { tags: nook.workspace.tags, unnamed: nook.label === '' ? nook.workspace : null },
      holder: holderOf.get(nook.path),
      run: () => navigate(hrefFor(nook.path)),
    }));

    const name = query.trim();
    const creating: Row[] = !adding
      ? []
      : createOffers(
          query,
          holderCandidates({
            items: picked,
            arcs,
            nooks: nooks.status === 'ready' ? nooks.workspaces : [],
            catalog,
            query: '',
            limit: Infinity,
          }),
        ).map((kind) => ({
          key: `new-${kind}`,
          label: `New ${kind} ‹${name}›`,
          meta: `holding ${picked.length}`,
          run: () => setDrafting({ kind, name }),
        }));

    // A register holds every match, so anything that matched can be reached;
    // past a screenful the rest folds behind one row, which ↵ unfolds in place.
    const folded = (name: string, rows: Row[]): Row[] =>
      expanded.has(name) || rows.length <= SHOWN + 1
        ? rows
        : [
            ...rows.slice(0, SHOWN),
            {
              key: `more-${name}`,
              label: `${rows.length - SHOWN} more`,
              more: true,
              run: () => setExpanded((e) => new Set(e).add(name)),
            },
          ];

    return [
      { name: 'arcs', rows: folded('arcs', matchedArcs) },
      { name: 'nooks', rows: folded('nooks', matchedNooks) },
      { name: 'tags', rows: folded('tags', matchedFolders) },
      { name: 'go to', rows: goTo },
      { name: 'do', rows: doThings },
      // Last, so ↵ never commits to something that has been taken off the board.
      { name: 'archived', rows: folded('archived', matchedArchived) },
      { name: 'new', rows: creating },
    ].filter((r) => r.rows.length > 0);
  }, [arcs, folders, query, expanded, navigate, theme, adding, picked, nooks, catalog, scratch, newScratch, onUsed]);

  const flatRows = registers.flatMap((r) => r.rows);
  const active = flatRows[Math.min(cursor, flatRows.length - 1)];

  useEffect(() => {
    body.current?.querySelector('.palette-row.on')?.scrollIntoView?.({ block: 'nearest' });
  }, [active?.key]);

  const addTo = (holder: HolderCandidate) => {
    void addItems(holder, picked);
    onUsed();
    setOpen(false);
  };

  if (drafting) {
    return (
      <NewHolderDraft
        kind={drafting.kind}
        initialName={drafting.name}
        items={picked}
        nameTaken={(name) => nameTaken(name, arcs)}
        onCancel={() => setDrafting(null)}
        onCreate={(draft) => {
          void createHolding(drafting.kind, draft.name, draft.items, draft.status);
          onUsed();
          setDrafting(null);
        }}
      />
    );
  }

  if (!open) return null;

  return (
    <div className="palette-scrim" onPointerDown={() => setOpen(false)}>
      <div className="palette" onPointerDown={(e) => e.stopPropagation()} role="dialog">
        <div className="palette-field">
          <svg className="palette-glyph" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" />
            <path d="M16 16l4.5 4.5" />
          </svg>
          <input
            autoFocus
            value={query}
            placeholder={adding ? `Open, or add ${picked.length} to…` : 'Find an arc or a tag'}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
              setExpanded(new Set());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, flatRows.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              }
              if (e.key === 'Enter' && active) {
                e.preventDefault();
                // ⇧↵ adds the page's selection where the row says it can go.
                if (e.shiftKey && active.holder) addTo(active.holder);
                // ⌘↵ opens an arc's plane instead of its page.
                else if ((e.metaKey || e.ctrlKey) && active.arc) navigate(`${arcHref(active.arc.name)}/plane`);
                else active.run();
                if (!active.more) setOpen(false);
              }
            }}
          />
        </div>

        <div className="palette-body" ref={body}>
          {registers.map((register) => (
            <section key={register.name}>
              <h2 className="palette-register">{register.name.charAt(0).toUpperCase() + register.name.slice(1)}</h2>
              {register.rows.map((row) => (
                <div
                  key={row.key}
                  role="button"
                  tabIndex={-1}
                  className={`palette-row${row.more ? ' more' : ''}${row.key === active?.key ? ' on' : ''}`}
                  onPointerEnter={() => setCursor(flatRows.findIndex((r) => r.key === row.key))}
                  onClick={() => {
                    row.run();
                    if (!row.more) setOpen(false);
                  }}
                >
                  {row.arc &&
                    (row.arc.archived ? <ArchivedGlyph /> : <StatusGlyph status={row.arc.status} />)}
                  {row.nook && <TintedNookGlyph tags={row.nook.tags} />}
                  {row.folder && <Mark folder={row.folder} size={13} theme={theme} />}
                  <span className="palette-label">{row.nook?.unnamed ? <NookThumb nook={row.nook.unnamed} /> : row.label}</span>
                  {row.meta && <span className="palette-meta">{row.meta}</span>}
                  {row.holder && (
                    <button
                      type="button"
                      className="palette-add"
                      aria-label={`Add ${picked.length} to ${row.label || 'this nook'}`}
                      title={`Add ${picked.length} here · ⇧↵`}
                      onClick={(e) => {
                        e.stopPropagation();
                        addTo(row.holder!);
                      }}
                    >
                      +
                    </button>
                  )}
                </div>
              ))}
            </section>
          ))}
          {flatRows.length === 0 && <p className="palette-none">Nothing matches.</p>}
        </div>

        <div className="palette-foot">
          <span>
            <kbd>↵</kbd> open · <kbd>⌘↵</kbd> its plane
            {adding && (
              <>
                {' '}
                · <kbd>⇧↵</kbd> add {picked.length}
              </>
            )}
          </span>
          <span>Arcs and tags — searching thoughts is the phone's</span>
        </div>
      </div>
    </div>
  );
}
